import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Search, TrendingDown } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import type { CennikProdukt } from "@/features/contracts/contracts-api";
import { contractsApi } from "@/features/contracts/contracts-api";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { getCurrentUser } from "@/lib/auth-api";
import { formatPLN } from "@/lib/format";
import { ensureAuthenticated } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// Backend dopuszcza tylko te role (poza nimi 403). Guard klienta odwzorowuje to,
// zeby analityk/superadmin wchodzili, a reszta dostawala redirect do dashboardu.
// Wzorzec identyczny jak w cenniki-historia.tsx.
const ALLOWED_ROLES = new Set(["super_admin", "analyst"]);

export const Route = createFileRoute("/_auth/kartoteka/cennik-najtaniej")({
	component: CennikNajtaniejPage,
	beforeLoad: async () => {
		await ensureAuthenticated();
		const user = getCurrentUser();
		if (!user || !user.roles.some((r) => ALLOWED_ROLES.has(r))) {
			throw redirect({ to: "/dashboard" });
		}
	},
});

type Segment = "AW" | "AF";

/** Bezpieczna konwersja string->number (ceny przychodza jako string). null gdy brak/NaN. */
function toNum(v?: string): number | null {
	if (v == null || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

/** Cena z gwardia — "—" gdy brak/NaN. */
function cena(v?: string): string {
	const n = toNum(v);
	return n != null ? formatPLN(n) : "—";
}

/** VAT jako np. "23%" — "—" gdy brak. */
function vatPct(v?: string): string {
	const n = toNum(v);
	return n != null ? `${n}%` : "—";
}

// ── Globalna wyszukiwarka produktu (po wszystkich hurtowniach) ────────────────
function ProduktGlobalnyPicker({
	selected,
	onSelect,
}: {
	selected: CennikProdukt | null;
	onSelect: (p: CennikProdukt | null) => void;
}) {
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const debounced = useDebounce(query, 300);

	const { data: results = [], isFetching } = useQuery({
		queryKey: ["cenniki", "produkty-globalne", debounced],
		queryFn: () => contractsApi.searchProduktGlobalny(debounced),
		enabled: open && debounced.trim().length > 0,
	});

	return (
		<div className="relative">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
				<Input
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onBlur={() => {
						// Opoznienie zamkniecia — pozwala klik w wynik zadzialac.
						window.setTimeout(() => setOpen(false), 150);
					}}
					placeholder="Szukaj po nazwie lub BLOZ…"
					className="pl-10 bg-background"
				/>
			</div>
			{selected && (
				<div className="mt-1.5 flex items-center gap-2 text-xs">
					<Badge className="font-mono text-[11px]">{selected.bloz07}</Badge>
					<span className="text-muted-foreground">{selected.nazwa || "—"}</span>
				</div>
			)}
			{open && (
				<div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-md border border-border bg-card shadow-sm">
					{debounced.trim().length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Wpisz frazę…</div>
					) : isFetching ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Szukam…</div>
					) : results.length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Brak wyników.</div>
					) : (
						results.map((p) => (
							<button
								key={p.bloz07}
								type="button"
								onMouseDown={(e) => {
									// onMouseDown (nie onClick) — odpala sie przed blur inputa.
									e.preventDefault();
									onSelect(p);
									setQuery(p.nazwa || p.bloz07);
									setOpen(false);
								}}
								className="flex w-full flex-col items-start gap-0.5 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-muted/60"
							>
								<span className="text-sm">{p.nazwa || "(bez nazwy)"}</span>
								<span className="font-mono text-[11px] text-muted-foreground">{p.bloz07}</span>
							</button>
						))
					)}
				</div>
			)}
		</div>
	);
}

function CennikNajtaniejPage() {
	const [produkt, setProdukt] = useState<CennikProdukt | null>(null);
	const [segment, setSegment] = useState<Segment>("AW");

	const canQuery = produkt != null;

	const {
		data: oferty = [],
		isFetching,
		isLoading,
	} = useQuery({
		queryKey: ["cenniki", "najtaniej", produkt?.bloz07, segment],
		queryFn: () => contractsApi.getNajtaniej(produkt?.bloz07 as string, segment),
		enabled: canQuery,
	});

	// Telefon: wyszukiwarka produktu zostaje na wierzchu (ekran zaczyna sie od niej),
	// segment AW/AF w chipie i arkuszu filtrow.
	const isBelowMd = useIsBelowMd();
	const mobileSegmentToggle = (
		<div className="flex rounded-md border border-border bg-background p-0.5">
			{(["AW", "AF"] as const).map((s) => (
				<button
					key={s}
					type="button"
					onClick={() => setSegment(s)}
					aria-pressed={segment === s}
					className={`min-h-9 flex-1 rounded px-3 text-sm font-medium transition-colors ${
						segment === s
							? "bg-primary text-primary-foreground"
							: "text-muted-foreground hover:bg-muted"
					}`}
				>
					{s}
				</button>
			))}
		</div>
	);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Najtańszy dostawca"
					description="Cena netto (oferta) danego BLOZ u wszystkich hurtowni — AW lub AF (najtańsza u góry)."
				/>
			</div>

			{isBelowMd && (
				<div className="px-3 pt-2 pb-2 border-t bg-card/50 [&_input]:h-10">
					<ProduktGlobalnyPicker selected={produkt} onSelect={setProdukt} />
				</div>
			)}
			<MobileFilters
				chips={[
					{
						id: "segment",
						label: "Segment",
						value: `Segment ${segment}`,
						active: segment !== "AW",
						picker: mobileSegmentToggle,
					},
				]}
				sheetContent={<MobileFilterField label="Segment">{mobileSegmentToggle}</MobileFilterField>}
				onClear={() => setSegment("AW")}
				resultLabel={canQuery ? `Pokaż ${oferty.length} ofert` : "Pokaż oferty"}
			>
				{/* Toolbar kontrolek */}
				<div className="px-6 py-3 border-y bg-card/50 grid grid-cols-1 gap-4 md:grid-cols-3">
					<div className="flex flex-col gap-1.5 md:col-span-2">
						<Label>Produkt</Label>
						<ProduktGlobalnyPicker selected={produkt} onSelect={setProdukt} />
					</div>

					<div className="flex flex-col gap-1.5">
						<Label>Segment</Label>
						<div className="inline-flex rounded-md border border-border bg-background p-0.5">
							{(["AW", "AF"] as const).map((s) => (
								<button
									key={s}
									type="button"
									onClick={() => setSegment(s)}
									className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors ${
										segment === s
											? "bg-primary text-primary-foreground"
											: "text-muted-foreground hover:bg-muted"
									}`}
								>
									{s}
								</button>
							))}
						</div>
					</div>
				</div>
			</MobileFilters>

			<div className="flex-1 min-h-0 overflow-auto p-6 pt-4 space-y-4 max-md:p-3">
				{!canQuery ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wybierz produkt.
						</CardContent>
					</Card>
				) : isLoading || isFetching ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wczytywanie…
						</CardContent>
					</Card>
				) : oferty.length === 0 ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Brak ofert dla wybranego produktu w segmencie {segment}.
						</CardContent>
					</Card>
				) : (
					<Card>
						<CardContent className="px-4 py-4 max-md:px-3 max-md:py-3">
							<div className="overflow-auto rounded-md border border-border">
								<table className="w-full text-xs border-collapse">
									<thead>
										<tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
											<th className="px-3 py-2 font-medium">Hurtownia</th>
											<th className="px-3 py-2 font-medium">Segment</th>
											<th className="px-3 py-2 font-medium text-right">Cena netto</th>
											<th className="px-3 py-2 font-medium text-right">VAT</th>
										</tr>
									</thead>
									<tbody>
										{oferty.map((r, i) => {
											const najtaniej = i === 0;
											return (
												<tr
													// biome-ignore lint/suspicious/noArrayIndexKey: oferty tylko do odczytu; dostawca i segment moga sie powtorzyc.
													key={`${r.idfdost}-${r.segment}-${i}`}
													className={`border-b border-border last:border-0 ${
														najtaniej ? "bg-success/10 hover:bg-success/15" : "hover:bg-muted/50"
													}`}
												>
													<td className="px-3 py-2">
														<div className="flex items-center gap-2">
															<span>{r.nazwa || `#${r.idfdost}`}</span>
															{najtaniej && (
																<Badge className="gap-1 border-success/30 bg-success/15 text-success text-[10px] uppercase">
																	<TrendingDown className="size-3" />
																	najtaniej
																</Badge>
															)}
														</div>
													</td>
													<td className="px-3 py-2">
														<Badge className="uppercase text-[11px]">
															{r.segment === "baza" ? "AW" : "AF"}
														</Badge>
													</td>
													<td className="px-3 py-2 text-right font-mono tabular-nums">
														{cena(r.cenaNetto)}
													</td>
													<td className="px-3 py-2 text-right tabular-nums">{vatPct(r.vat)}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
}
